/**
 * Admin Projects Page - Refactored
 * Main admin panel with tabs for Projects, Leads, Contact, Team, QA, Revenue, and Theme Requests
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FolderOpen, Users, UserCog, Bug, DollarSign, TestTube, Palette, Lightbulb, Sparkles, MessageSquare, Book, UserPlus, Mail } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Import refactored components
import { 
  AdminHeader, 
  ProjectsTab, 
  LeadsTab, 
  TeamTab, 
  QATab, 
  RevenueTab,
  ContactTab,
  DocumentationTab,
  API 
} from '@/components/admin';
import { UserDetailModal } from '@/components/admin/UserDetailModal';

// Sortable header component - defined outside to avoid re-creation during render
const SortableHeader = ({ label, sortKey, sortConfig, onSort, className = '' }) => (
  <th 
    className={`py-2 px-3 text-left text-xs font-medium text-zinc-500 uppercase cursor-pointer hover:bg-zinc-100 select-none ${className}`}
    onClick={() => onSort(sortKey)}
  >
    <div className="flex items-center gap-1">
      {label}
      {sortConfig.key === sortKey && (
        <span className="text-amber-500">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
      )}
    </div>
  </th>
);

const AdminProjectsPage = () => {
  const navigate = useNavigate();
  
  // Authentication & User State
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  
  // Tab State - Now with main tabs and sub tabs
  const [activeMainTab, setActiveMainTab] = useState('customers');
  const [activeSubTab, setActiveSubTab] = useState('projects');
  
  // Legacy activeTab for compatibility with existing code
  const activeTab = activeSubTab;
  
  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Projects State
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [showCreateForCustomer, setShowCreateForCustomer] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  
  // Leads State
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({});
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  
  // Contact Submissions State
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactStats, setContactStats] = useState({});
  const [contactLoading, setContactLoading] = useState(false);
  
  // Team State
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', name: '', role: 'design_reviewer' });
  
  // QA State
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [bugsStats, setBugsStats] = useState({});
  const [bugStatusFilter, setBugStatusFilter] = useState('all');
  const [selectedBug, setSelectedBug] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(100);
  
  // Revenue State
  const [revenueSummary, setRevenueSummary] = useState({});
  const [revenueCustomers, setRevenueCustomers] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [selectedRevenueCustomer, setSelectedRevenueCustomer] = useState(null);
  const [customerPayments, setCustomerPayments] = useState([]);

  // Theme Requests State
  const [themeRequests, setThemeRequests] = useState([]);
  const [themeRequestsLoading, setThemeRequestsLoading] = useState(false);
  const [pendingThemeRequests, setPendingThemeRequests] = useState(0);

  // Feature Requests State
  const [featureRequests, setFeatureRequests] = useState([]);
  const [featureRequestsStats, setFeatureRequestsStats] = useState({});
  const [featureRequestsLoading, setFeatureRequestsLoading] = useState(false);
  const [analyzingFeatures, setAnalyzingFeatures] = useState(false);

  // Registered Users State
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // User Detail Modal State
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [userDetailData, setUserDetailData] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  // Initial data fetching
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (userRole) {
      fetchProjects();
      fetchReviewers();
      // Pre-fetch leads and contacts for faster tab switching
      fetchLeads();
      fetchContactSubmissions();
    }
  }, [userRole]);

  // Auth check
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const user = response.data;
      
      // Check admin or team role
      const teamResponse = await axios.get(`${API}/projects/admin/team`, { withCredentials: true });
      const teamMembers = teamResponse.data.team || [];
      const teamMember = teamMembers.find(m => m.member_email === user.email);
      
      if (user.email === 'pgundepudi@gmail.com') {
        setUserRole('admin');
      } else if (teamMember) {
        setUserRole(teamMember.role);
      } else {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      
      setLoading(false);
    } catch (error) {
      navigate('/');
    }
  };

  // ========== PROJECTS FUNCTIONS ==========
  const fetchProjects = async () => {
    try {
      const [projectsRes, statsRes] = await Promise.all([
        axios.get(`${API}/projects/admin/all`, { withCredentials: true }),
        axios.get(`${API}/projects/admin/stats`, { withCredentials: true })
      ]);
      setProjects(projectsRes.data.projects || []);
      setProjectStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    setSelectedProject(projectId);
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/projects/admin/project/${projectId}`, { withCredentials: true });
      setProjectDetails(response.data);
    } catch (error) {
      toast.error('Failed to load project details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/projects/admin/project/${projectId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchProjects();
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const assignProject = async (projectId, email) => {
    try {
      await axios.put(`${API}/projects/admin/project/${projectId}/assign`, { assigned_to: email || null }, { withCredentials: true });
      toast.success(email ? 'Project assigned' : 'Assignment removed');
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error('Failed to assign project');
    }
  };

  const fetchReviewers = async () => {
    try {
      const response = await axios.get(`${API}/projects/admin/design-reviewers`, { withCredentials: true });
      setReviewers(response.data.reviewers || []);
    } catch (error) {
      console.error('Error fetching reviewers:', error);
    }
  };

  const startCreateForCustomer = () => {
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    navigate(`/briefing?adminCreateFor=${encodeURIComponent(customerEmail)}`);
  };

  const generatePaymentLink = async (projectId, plan, pageCount) => {
    setGeneratingPaymentLink(true);
    try {
      await axios.post(`${API}/projects/admin/project/${projectId}/generate-payment-link`, {
        plan,
        page_count: pageCount,
        custom_amount: null
      }, { withCredentials: true });
      toast.success('Payment link generated');
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error('Failed to generate payment link');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const copyPaymentLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const sendPaymentLink = async (projectId) => {
    setSendingPaymentLink(true);
    try {
      await axios.post(`${API}/projects/admin/project/${projectId}/send-payment-link`, {}, { withCredentials: true });
      toast.success('Payment link sent to customer');
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error('Failed to send payment link');
    } finally {
      setSendingPaymentLink(false);
    }
  };

  // ========== LEADS FUNCTIONS ==========
  const fetchLeads = async () => {
    console.log('[DEBUG] fetchLeads called at', new Date().toISOString());
    setLeadsLoading(true);
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.get(`${API}/leads`, { 
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        params: { _t: Date.now() }
      });
      console.log('[DEBUG] Leads response:', response.data);
      setLeads(response.data.leads || []);
      setLeadsStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status }, { withCredentials: true });
      toast.success('Lead status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead status');
    }
  };

  // ========== CONTACT SUBMISSIONS FUNCTIONS ==========
  const fetchContactSubmissions = async () => {
    console.log('[DEBUG] fetchContactSubmissions called at', new Date().toISOString());
    setContactLoading(true);
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.get(`${API}/contact/admin`, { 
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        params: { _t: Date.now() }
      });
      console.log('[DEBUG] Contacts response:', response.data);
      setContactSubmissions(response.data.submissions || []);
      setContactStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    } finally {
      setContactLoading(false);
    }
  };

  const updateContactStatus = async (submissionId, status) => {
    try {
      await axios.patch(`${API}/contact/${submissionId}/status`, { status }, { withCredentials: true });
      toast.success('Contact status updated');
      fetchContactSubmissions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ========== TEAM FUNCTIONS ==========
  const fetchTeam = async () => {
    setTeamLoading(true);
    try {
      const response = await axios.get(`${API}/projects/admin/team`, { withCredentials: true });
      setTeam(response.data.team || []);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setTeamLoading(false);
    }
  };

  const addTeamMember = async () => {
    if (!newMember.email) {
      toast.error('Email is required');
      return;
    }
    try {
      await axios.post(`${API}/projects/admin/team/add`, newMember, { withCredentials: true });
      toast.success('Team member added');
      setNewMember({ email: '', name: '', role: 'design_reviewer' });
      setShowAddMember(false);
      fetchTeam();
      fetchReviewers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const removeTeamMember = async (email) => {
    if (!window.confirm(`Remove ${email} from team?`)) return;
    try {
      await axios.delete(`${API}/projects/admin/team/${email}`, { withCredentials: true });
      toast.success('Member removed');
      fetchTeam();
      fetchReviewers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // ========== QA FUNCTIONS ==========
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await axios.get(`${API}/qa/customers`, { withCredentials: true });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const startImpersonation = async (customer) => {
    try {
      await axios.post(`${API}/qa/start-impersonation`, { target_user_id: customer.user_id }, { withCredentials: true });
      toast.success(`Now testing as ${customer.name || customer.email}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to start impersonation');
    }
  };

  const qaAddCredits = async (userId, credits) => {
    try {
      const response = await axios.post(`${API}/projects/admin/qa/add-credits`, {
        user_id: userId,
        credits: credits
      }, { withCredentials: true });
      toast.success(`Added ${credits} credits. New balance: ${response.data.new_balance}`);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to add credits');
    }
  };

  const qaSkipPayment = async (projectId) => {
    try {
      await axios.post(`${API}/projects/admin/qa/skip-payment`, { project_id: projectId }, { withCredentials: true });
      toast.success('Payment skipped (QA)');
      fetchProjectDetails(projectId);
    } catch (error) {
      toast.error('Failed to skip payment');
    }
  };

  const fetchBugs = async () => {
    setBugsLoading(true);
    try {
      const response = await axios.get(`${API}/qa/bugs?status=${bugStatusFilter}`, { withCredentials: true });
      setBugs(response.data.bugs || []);
      setBugsStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching bugs:', error);
    } finally {
      setBugsLoading(false);
    }
  };

  const updateBugStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/qa/bugs/${ticketId}/status`, { status }, { withCredentials: true });
      toast.success('Bug status updated');
      fetchBugs();
      if (selectedBug?.ticket_id === ticketId) {
        setSelectedBug({ ...selectedBug, status });
      }
    } catch (error) {
      toast.error('Failed to update bug status');
    }
  };

  // ========== REVENUE FUNCTIONS ==========
  const fetchRevenueSummary = async () => {
    setRevenueLoading(true);
    try {
      const [summaryRes, customersRes] = await Promise.all([
        axios.get(`${API}/revenue/summary`, { withCredentials: true }),
        axios.get(`${API}/revenue/customers`, { withCredentials: true })
      ]);
      setRevenueSummary(summaryRes.data);
      setRevenueCustomers(customersRes.data.customers || []);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchCustomerPayments = async (userId) => {
    const customer = revenueCustomers.find(c => c.user_id === userId);
    setSelectedRevenueCustomer(customer);
    try {
      const response = await axios.get(`${API}/revenue/customer/${userId}/payments`, { withCredentials: true });
      setCustomerPayments(response.data.payments || []);
    } catch (error) {
      toast.error('Failed to fetch payments');
    }
  };

  const generateInvoice = async (paymentId) => {
    try {
      await axios.post(`${API}/revenue/invoices/generate`, { payment_id: paymentId }, { withCredentials: true });
      toast.success('Invoice generated');
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  // ========== THEME REQUESTS FUNCTIONS ==========
  const fetchThemeRequests = async () => {
    setThemeRequestsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/theme-requests`, { withCredentials: true });
      setThemeRequests(response.data.requests || []);
      setPendingThemeRequests(response.data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching theme requests:', error);
    } finally {
      setThemeRequestsLoading(false);
    }
  };

  const updateThemeRequestStatus = async (requestId, status, notes = '') => {
    try {
      await axios.put(`${API}/admin/theme-requests/${requestId}/status`, 
        { status, notes },
        { withCredentials: true }
      );
      toast.success(`Theme request marked as ${status}`);
      fetchThemeRequests();
    } catch (error) {
      toast.error('Failed to update theme request');
    }
  };

  // ========== FEATURE REQUESTS FUNCTIONS ==========
  const fetchFeatureRequests = async () => {
    setFeatureRequestsLoading(true);
    try {
      const response = await axios.get(`${API}/feature-requests/admin/all`, { withCredentials: true });
      setFeatureRequests(response.data.requests || []);
      setFeatureRequestsStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching feature requests:', error);
    } finally {
      setFeatureRequestsLoading(false);
    }
  };

  const analyzeFeatureRequests = async () => {
    setAnalyzingFeatures(true);
    try {
      const response = await axios.post(`${API}/feature-requests/admin/analyze`, {}, { withCredentials: true });
      toast.success(response.data.message || 'AI analysis complete');
      fetchFeatureRequests();
    } catch (error) {
      toast.error('Failed to analyze feature requests');
    } finally {
      setAnalyzingFeatures(false);
    }
  };

  const updateFeatureRequestStatus = async (requestId, status, notes = '') => {
    try {
      await axios.put(`${API}/feature-requests/admin/${requestId}/status`, 
        { status, admin_notes: notes },
        { withCredentials: true }
      );
      toast.success(`Feature request updated`);
      fetchFeatureRequests();
    } catch (error) {
      toast.error('Failed to update feature request');
    }
  };

  // ========== REGISTERED USERS ==========
  const fetchRegisteredUsers = async () => {
    console.log('[DEBUG] fetchRegisteredUsers called at', new Date().toISOString());
    setUsersLoading(true);
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.get(`${API}/auth/admin/users`, { 
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        params: { _t: Date.now() }
      });
      console.log('[DEBUG] Users response:', response.data);
      setRegisteredUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load registered users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch user detail for CRM-style modal
  const fetchUserDetail = async (userId) => {
    setSelectedUserDetail(userId);
    setUserDetailLoading(true);
    setUserDetailData(null);
    try {
      const response = await axios.get(`${API}/auth/admin/users/${userId}`, { withCredentials: true });
      setUserDetailData(response.data);
    } catch (error) {
      console.error('Error fetching user detail:', error);
      toast.error('Failed to load user details');
      setSelectedUserDetail(null);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const closeUserDetailModal = () => {
    setSelectedUserDetail(null);
    setUserDetailData(null);
  };

  // ========== REFRESH HANDLER ==========
  const handleRefresh = () => {
    console.log('[DEBUG] handleRefresh called - mainTab:', activeMainTab, 'subTab:', activeSubTab);
    
    // Refresh based on main tab first
    if (activeMainTab === 'contacts') {
      fetchContactSubmissions();
    } else if (activeMainTab === 'leads') {
      fetchLeads();
    } else if (activeMainTab === 'customers') {
      // Refresh based on sub tab within customers
      if (activeSubTab === 'projects') fetchProjects();
      else if (activeSubTab === 'users') fetchRegisteredUsers();
      else if (activeSubTab === 'themes') fetchThemeRequests();
      else if (activeSubTab === 'revenue') fetchRevenueSummary();
      else if (activeSubTab === 'features') fetchFeatureRequests();
    } else if (activeMainTab === 'team') {
      if (activeSubTab === 'team') fetchTeam();
      else if (activeSubTab === 'qa') { fetchCustomers(); fetchBugs(); }
    }
    
    toast.success('Data refreshed');
  };

  // Handle main tab change - set default sub tab
  const handleMainTabChange = (mainTab) => {
    setActiveMainTab(mainTab);
    if (mainTab === 'contacts') {
      setActiveSubTab('contact');
      fetchContactSubmissions();
    } else if (mainTab === 'leads') {
      setActiveSubTab('leads');
      fetchLeads();
    } else if (mainTab === 'customers') {
      setActiveSubTab('users');
      fetchRegisteredUsers();
    } else if (mainTab === 'team') {
      setActiveSubTab('team');
      fetchTeam();
    }
  };

  // Handle sub tab change
  const handleSubTabChange = (subTab) => {
    setActiveSubTab(subTab);
    if (subTab === 'projects') fetchProjects();
    else if (subTab === 'users') fetchRegisteredUsers();
    else if (subTab === 'themes') fetchThemeRequests();
    else if (subTab === 'revenue') fetchRevenueSummary();
    else if (subTab === 'features') fetchFeatureRequests();
    else if (subTab === 'team') fetchTeam();
    else if (subTab === 'qa') { fetchCustomers(); fetchBugs(); }
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!sortConfig.key || !data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle dates
      if (sortConfig.key.includes('date') || sortConfig.key.includes('created_at') || sortConfig.key.includes('_at')) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle numbers
      else if (typeof aVal === 'number' || typeof bVal === 'number') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // Handle strings
      else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <AdminHeader 
        userRole={userRole}
        activeTab={activeSubTab}
        onRefresh={handleRefresh}
      />

      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Main Level Tabs */}
          <div className="flex gap-2 mb-4 border-b border-zinc-200 pb-2">
            <button
              onClick={() => handleMainTabChange('contacts')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeMainTab === 'contacts' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Contacts
              {contactStats.new > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{contactStats.new}</span>
              )}
            </button>
            <button
              onClick={() => handleMainTabChange('leads')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeMainTab === 'leads' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads
              {leadsStats.new > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{leadsStats.new}</span>
              )}
            </button>
            <button
              onClick={() => handleMainTabChange('customers')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeMainTab === 'customers' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              <FolderOpen className="w-4 h-4 inline mr-2" />
              Customers
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => handleMainTabChange('team')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeMainTab === 'team' 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                <UserCog className="w-4 h-4 inline mr-2" />
                Team
              </button>
            )}
            {userRole === 'admin' && (
              <button
                onClick={() => handleMainTabChange('docs')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeMainTab === 'docs' 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                <Book className="w-4 h-4 inline mr-2" />
                Docs
              </button>
            )}
          </div>

          {/* Sub Tabs based on Main Tab */}
          {activeMainTab === 'customers' && (
            <Tabs value={activeSubTab} onValueChange={handleSubTabChange}>
              <TabsList className="mb-6 flex-wrap">
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="w-4 h-4" /> Users
                </TabsTrigger>
                <TabsTrigger value="projects" className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" /> Projects
                  {projectStats.pending_review > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">{projectStats.pending_review}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="themes" className="flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Theme Requests
                  {pendingThemeRequests > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">{pendingThemeRequests}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="revenue" className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Revenue
                </TabsTrigger>
                <TabsTrigger value="features" className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Feature Requests
                  {featureRequestsStats.pending > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">{featureRequestsStats.pending}</span>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Contacts Main Tab Content */}
          {activeMainTab === 'contacts' && (
            <ContactTab 
              submissions={contactSubmissions}
              stats={contactStats}
              loading={contactLoading}
              onUpdateStatus={updateContactStatus}
            />
          )}

          {/* Leads Main Tab Content */}
          {activeMainTab === 'leads' && (
            <LeadsTab 
              leads={leads}
              leadsStats={leadsStats}
              leadsLoading={leadsLoading}
              leadStatusFilter={leadStatusFilter}
              setLeadStatusFilter={setLeadStatusFilter}
              updateLeadStatus={updateLeadStatus}
            />
          )}

          {activeMainTab === 'team' && userRole === 'admin' && (
            <Tabs value={activeSubTab} onValueChange={handleSubTabChange}>
              <TabsList className="mb-6 flex-wrap">
                <TabsTrigger value="team" className="flex items-center gap-2">
                  <UserCog className="w-4 h-4" /> Team Members
                </TabsTrigger>
                <TabsTrigger value="qa" className="flex items-center gap-2">
                  <Bug className="w-4 h-4" /> QA
                  {bugsStats.open > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs">{bugsStats.open}</span>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* QA only role - show QA directly */}
          {userRole === 'qa' && activeMainTab === 'team' && (
            <div className="mb-6">
              <span className="text-zinc-600 text-sm">QA Tools</span>
            </div>
          )}

          {/* Tab Contents */}
          {/* Projects Tab */}
          {activeMainTab === 'customers' && activeSubTab === 'projects' && (
              <ProjectsTab 
                projects={projects}
                projectStats={projectStats}
                selectedProject={selectedProject}
                projectDetails={projectDetails}
                loadingDetails={loadingDetails}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                updatingStatus={updatingStatus}
                reviewers={reviewers}
                userRole={userRole}
                showCreateForCustomer={showCreateForCustomer}
                setShowCreateForCustomer={setShowCreateForCustomer}
                customerEmail={customerEmail}
                setCustomerEmail={setCustomerEmail}
                generatingPaymentLink={generatingPaymentLink}
                sendingPaymentLink={sendingPaymentLink}
                sortConfig={sortConfig}
                handleSort={handleSort}
                startCreateForCustomer={startCreateForCustomer}
                fetchProjectDetails={fetchProjectDetails}
                updateProjectStatus={updateProjectStatus}
                assignProject={assignProject}
                qaSkipPayment={qaSkipPayment}
                generatePaymentLink={generatePaymentLink}
                sendPaymentLink={sendPaymentLink}
                copyPaymentLink={copyPaymentLink}
              />
          )}

          {/* Team Tab */}
          {activeMainTab === 'team' && activeSubTab === 'team' && (
              <TeamTab 
                team={team}
                teamLoading={teamLoading}
                showAddMember={showAddMember}
                setShowAddMember={setShowAddMember}
                newMember={newMember}
                setNewMember={setNewMember}
                addTeamMember={addTeamMember}
                removeTeamMember={removeTeamMember}
              />
          )}

          {/* QA Tab */}
          {activeMainTab === 'team' && activeSubTab === 'qa' && (
              <QATab 
                userRole={userRole}
                customers={customers}
                customersLoading={customersLoading}
                bugs={bugs}
                bugsLoading={bugsLoading}
                bugsStats={bugsStats}
                bugStatusFilter={bugStatusFilter}
                setBugStatusFilter={setBugStatusFilter}
                selectedBug={selectedBug}
                setSelectedBug={setSelectedBug}
                startImpersonation={startImpersonation}
                qaAddCredits={qaAddCredits}
                fetchBugs={fetchBugs}
                updateBugStatus={updateBugStatus}
              />
          )}

          {/* Revenue Tab */}
          {activeMainTab === 'customers' && activeSubTab === 'revenue' && userRole === 'admin' && (
                <RevenueTab 
                  revenueSummary={revenueSummary}
                  revenueCustomers={revenueCustomers}
                  revenueLoading={revenueLoading}
                  selectedRevenueCustomer={selectedRevenueCustomer}
                  customerPayments={customerPayments}
                  fetchCustomerPayments={fetchCustomerPayments}
                  generateInvoice={generateInvoice}
                />
          )}

          {/* Theme Requests Tab */}
          {activeMainTab === 'customers' && activeSubTab === 'themes' && userRole === 'admin' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl border border-zinc-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-bold text-zinc-900">Theme Installation Requests</h2>
                        <p className="text-sm text-zinc-500">Customers have requested these WordPress.org themes to be installed</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          {pendingThemeRequests} pending
                        </span>
                      </div>
                    </div>

                    {themeRequestsLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                      </div>
                    ) : themeRequests.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No theme installation requests yet
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {themeRequests.map((req) => (
                          <div 
                            key={req.request_id}
                            className={`border rounded-lg p-4 ${
                              req.status === 'pending' ? 'border-amber-200 bg-amber-50' :
                              req.status === 'installed' ? 'border-green-200 bg-green-50' :
                              'border-red-200 bg-red-50'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Theme Screenshot */}
                              {req.screenshot_url && (
                                <img 
                                  src={req.screenshot_url} 
                                  alt={req.theme_name}
                                  className="w-32 h-24 object-cover rounded-lg border border-zinc-200"
                                />
                              )}
                              
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h3 className="font-bold text-zinc-900">{req.theme_name}</h3>
                                    <a 
                                      href={req.theme_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-sm text-indigo-600 hover:underline"
                                    >
                                      {req.theme_url}
                                    </a>
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    req.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                                    req.status === 'installed' ? 'bg-green-200 text-green-800' :
                                    'bg-red-200 text-red-800'
                                  }`}>
                                    {req.status}
                                  </span>
                                </div>
                                
                                <div className="mt-2 text-sm text-zinc-600">
                                  <p><strong>Project:</strong> {req.project_name} ({req.project_id})</p>
                                  <p><strong>Customer:</strong> {req.customer_name} ({req.customer_email})</p>
                                  <p><strong>Requested:</strong> {new Date(req.created_at).toLocaleString()}</p>
                                </div>

                                {req.status === 'pending' && (
                                  <div className="mt-3 flex items-center gap-2">
                                    <button
                                      onClick={() => updateThemeRequestStatus(req.request_id, 'installed')}
                                      className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                                    >
                                      Mark as Installed
                                    </button>
                                    <button
                                      onClick={() => updateThemeRequestStatus(req.request_id, 'rejected', 'Theme not available or incompatible')}
                                      className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                    <a
                                      href={`https://wowcx.io/wp-admin/theme-install.php?search=${req.theme_slug}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                                    >
                                      Install in WordPress
                                    </a>
                                  </div>
                                )}

                                {req.admin_notes && (
                                  <p className="mt-2 text-xs text-zinc-500 italic">Note: {req.admin_notes}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
          )}

          {/* Registered Users Tab */}
          {activeMainTab === 'customers' && activeSubTab === 'users' && userRole === 'admin' && (
                <div className="bg-white rounded-lg border shadow-sm">
                  <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-zinc-900">Registered Users</h2>
                        <p className="text-sm text-zinc-600 mt-1">All users who have signed up for the platform</p>
                      </div>
                      <span className="text-sm text-zinc-500">{registeredUsers.length} users</span>
                    </div>
                  </div>

                  {usersLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  ) : registeredUsers.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      No registered users found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-zinc-50 border-b">
                          <tr>
                            <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={handleSort} className="px-4" />
                            <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} className="px-4" />
                            <SortableHeader label="Registered" sortKey="registered_at" sortConfig={sortConfig} onSort={handleSort} className="px-4" />
                            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Workspace(s)</th>
                            <SortableHeader label="Role" sortKey="role" sortConfig={sortConfig} onSort={handleSort} className="px-4" />
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {sortData(registeredUsers).map((u) => (
                            <tr 
                              key={u.user_id} 
                              className="hover:bg-indigo-50 cursor-pointer transition-colors"
                              onClick={() => fetchUserDetail(u.user_id)}
                            >
                              <td className="px-4 py-3 text-sm text-zinc-900 font-medium">{u.email}</td>
                              <td className="px-4 py-3 text-sm text-zinc-700">{u.name || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-zinc-500">
                                {u.registered_at ? new Date(u.registered_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {u.workspaces && u.workspaces.length > 0 ? (
                                  <div className="space-y-1">
                                    {u.workspaces.map((ws, i) => (
                                      <div key={i} className="flex items-center gap-1">
                                        <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{ws.display_id || ws.workspace_id}</span>
                                        <span className="text-zinc-600">{ws.name}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          ws.role === 'owner' ? 'bg-indigo-100 text-indigo-700' : 'bg-zinc-100 text-zinc-600'
                                        }`}>{ws.role}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-zinc-400">No workspace</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  u.role === 'Admin' ? 'bg-purple-100 text-purple-800' :
                                  u.role === 'QA Tester' ? 'bg-blue-100 text-blue-800' :
                                  u.role === 'Design Reviewer' ? 'bg-green-100 text-green-800' :
                                  'bg-zinc-100 text-zinc-700'
                                }`}>
                                  {u.role}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
          )}

          {/* Feature Requests Tab */}
          {activeMainTab === 'customers' && activeSubTab === 'features' && userRole === 'admin' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl border border-zinc-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-bold text-zinc-900">Feature Requests</h2>
                        <p className="text-sm text-zinc-500">Customer-submitted feature ideas with AI prioritization</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-sm">
                          <span className="text-zinc-500">Total: </span>
                          <span className="font-semibold">{featureRequestsStats.total || 0}</span>
                          <span className="mx-2 text-zinc-300">|</span>
                          <span className="text-zinc-500">Votes: </span>
                          <span className="font-semibold">{featureRequestsStats.total_votes || 0}</span>
                        </div>
                        <button
                          onClick={analyzeFeatureRequests}
                          disabled={analyzingFeatures}
                          className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50"
                        >
                          {analyzingFeatures ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          {analyzingFeatures ? 'Analyzing...' : 'AI Analyze & Prioritize'}
                        </button>
                      </div>
                    </div>

                    {featureRequestsLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                      </div>
                    ) : featureRequests.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No feature requests yet
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {featureRequests.map((req, index) => (
                          <div 
                            key={req.request_id}
                            className={`border rounded-lg p-4 ${
                              req.status === 'planned' ? 'border-purple-200 bg-purple-50' :
                              req.status === 'in_progress' ? 'border-blue-200 bg-blue-50' :
                              req.status === 'completed' ? 'border-green-200 bg-green-50' :
                              req.status === 'rejected' ? 'border-red-200 bg-red-50' :
                              'border-zinc-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className="flex flex-col items-center gap-1 px-2 py-1 bg-zinc-100 rounded-lg">
                                  <span className="text-xs text-zinc-500">Priority</span>
                                  <span className="text-lg font-bold text-zinc-900">#{index + 1}</span>
                                  <span className="text-xs text-amber-600 font-medium">{req.priority_score || 50}</span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-zinc-900">{req.title}</h3>
                                    <span className="px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded text-xs">{req.category}</span>
                                  </div>
                                  <p className="text-sm text-zinc-600 mb-2">{req.description}</p>
                                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                                    <span>👤 {req.user_email}</span>
                                    <span>👍 {req.votes || 0} votes</span>
                                    <span>📅 {new Date(req.created_at).toLocaleDateString()}</span>
                                  </div>
                                  {req.ai_analysis && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                      <span className="font-medium text-amber-800">AI Analysis: </span>
                                      <span className="text-amber-700">{req.ai_analysis}</span>
                                      <div className="mt-1 flex gap-3 text-amber-600">
                                        <span>Relevance: {req.ai_relevance}/10</span>
                                        <span>Impact: {req.ai_impact}/10</span>
                                        <span>Effort: {req.ai_effort}/3</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium text-center ${
                                  req.status === 'pending' ? 'bg-zinc-200 text-zinc-800' :
                                  req.status === 'under_review' ? 'bg-blue-200 text-blue-800' :
                                  req.status === 'planned' ? 'bg-purple-200 text-purple-800' :
                                  req.status === 'in_progress' ? 'bg-amber-200 text-amber-800' :
                                  req.status === 'completed' ? 'bg-green-200 text-green-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {req.status}
                                </span>
                                <select
                                  value={req.status}
                                  onChange={(e) => updateFeatureRequestStatus(req.request_id, e.target.value)}
                                  className="text-xs border rounded px-2 py-1"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="under_review">Under Review</option>
                                  <option value="planned">Planned</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                  <option value="rejected">Not Planned</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
          )}

          {/* Documentation Tab */}
          {activeMainTab === 'docs' && userRole === 'admin' && (
            <DocumentationTab />
          )}
        </div>
      </main>
      
      {/* User Detail Modal (CRM-style) */}
      <UserDetailModal
        isOpen={!!selectedUserDetail}
        onClose={closeUserDetailModal}
        userData={userDetailData}
        loading={userDetailLoading}
      />
    </div>
  );
};

export default AdminProjectsPage;
